import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Minus,
  Plus,
  Info,
  Zap,
  CheckCircle,
  X,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import './PaymentMobile.css';

const PaymentMobile = () => {
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [localSubscriptionPlan, setLocalSubscriptionPlan] = useState('3months');
  const [localLines, setLocalLines] = useState(1);
  const [showPriceBreakdown, setShowPriceBreakdown] = useState(false);
  const [costs, setCosts] = useState({
    perLineCost: 0,
    verizonMobileCost: 0,
    estimatedTaxes: 0,
    activationFee: 0,
    circledinSubscriptionCost: 0,
    estimatedMonthlyVerizonTotal: 0,
    verizonTotalToday: 0,
    circledinTotal: 0,
    totalDueNow: 0
  });

  useEffect(() => {
    const storedPlan = localStorage.getItem('selectedPlan');
    if (storedPlan) {
      setSelectedPlan(JSON.parse(storedPlan));
      localStorage.removeItem('selectedPlan');
    }
  }, []);

  const calculateCosts = (plan, subscriptionType, lines) => {
    if (!plan || !plan.perLineCost) return {};

    const perLineCost = plan.perLineCost;
    const verizonMobileCost = perLineCost * lines;
    const estimatedTaxes = verizonMobileCost * 0.185;
    const activationFee = 35 * lines;
    const circledinSubscriptionCost = subscriptionType === 'annual' ? 96 : 36;

    const estimatedMonthlyVerizonTotal = verizonMobileCost + estimatedTaxes;
    const verizonTotalToday = verizonMobileCost + estimatedTaxes + activationFee;
    const circledinTotal = circledinSubscriptionCost;
    const totalDueNow = verizonTotalToday + circledinTotal;

    return {
      perLineCost,
      verizonMobileCost,
      estimatedTaxes,
      activationFee,
      circledinSubscriptionCost,
      estimatedMonthlyVerizonTotal,
      verizonTotalToday,
      circledinTotal,
      totalDueNow
    };
  };

  useEffect(() => {
    if (selectedPlan) {
      const newCosts = calculateCosts(selectedPlan, localSubscriptionPlan, localLines);
      setCosts(newCosts);
    }
  }, [selectedPlan, localSubscriptionPlan, localLines]);

  const handleSubscriptionChange = (newSubscription) => {
    setLocalSubscriptionPlan(newSubscription);
  };

  const handleLineChange = (change) => {
    const newLines = Math.max(1, localLines + change);
    setLocalLines(newLines);
  };

  const PriceBreakdown = ({ onClose }) => (
    <motion.div 
      className="payment-mobile-price-breakdown"
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 50 }}
    >
      <div className="payment-mobile-price-breakdown-header">
        <h3>Price breakdown</h3>
        <button onClick={onClose}><X size={20} /></button>
      </div>
      <div className="payment-mobile-price-breakdown-content">
        <div className="payment-mobile-breakdown-section">
          <h4>Your Plan Breakdown</h4>
          <CostItem label="Verizon Mobile Cost" value={costs.verizonMobileCost} />
          <CostItem label="Est. Verizon Taxes" value={costs.estimatedTaxes} />
          <CostItem label="Estimated Monthly Verizon Total" value={costs.estimatedMonthlyVerizonTotal} isTotal={true} />
        </div>
        
        <div className="payment-mobile-breakdown-section">
          <h4>What You Pay Today</h4>
          <CostItem label="Verizon Mobile Cost" value={costs.verizonMobileCost} />
          <CostItem label="Activation Fee (One-time)" value={costs.activationFee} />
          <CostItem label="Est. Verizon Taxes" value={costs.estimatedTaxes} />
          <CostItem label="Verizon Total" value={costs.verizonTotalToday} isTotal={true} />
        </div>
        
        <div className="payment-mobile-breakdown-section">
          <h4>Circledin Charges</h4>
          <CostItem 
            label={`Circledin Subscription (${localSubscriptionPlan === 'annual' ? 'Annual' : '3 Months'})`} 
            value={costs.circledinSubscriptionCost} 
          />
          <CostItem label="Circledin Total" value={costs.circledinTotal} isTotal={true} />
        </div>
        
        <CostItem label="Total Due Now" value={costs.totalDueNow} isTotal={true} className="payment-mobile-grand-total" />
      </div>
    </motion.div>
  );

  if (!selectedPlan) {
    return <div className="payment-mobile-container">Loading...</div>;
  }

  return (
    <div className="payment-mobile-container">
      <div className="payment-mobile-content">
        <motion.div 
          className="payment-mobile-circle-info"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h2>Morning Coffee <CheckCircle size={20} className="payment-mobile-verified-icon" /></h2>
          <p>10 Spots Left</p>
        </motion.div>
        
        <motion.div 
          className="payment-mobile-plan-slider"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="payment-mobile-slider-content">
            <h3>{selectedPlan.title || 'Selected Plan'}</h3>
            <p className="payment-mobile-plan-price">
              ${costs.perLineCost.toFixed(2)}/line/month
            </p>
            <p className="payment-mobile-plan-description">
              {selectedPlan.description || 'Experience unlimited data, talk, and text with our basic plan.'}
            </p>
          </div>
        </motion.div>

        <div className="payment-mobile-billing-cycle">
          Billing Cycle: September 12 - October 11
        </div>

        <h2 className="payment-mobile-section-title">Choose Your Circledin Subscription</h2>

        <div className="payment-mobile-subscription-options">
          <motion.button
            className={`payment-mobile-subscription-option ${localSubscriptionPlan === 'annual' ? 'payment-mobile-selected' : ''}`}
            onClick={() => handleSubscriptionChange('annual')}
            whileTap={{ scale: 0.95 }}
          >
            <h3 className="payment-mobile-subscription-title">Annual Plan</h3>
            <span className="payment-mobile-price">$8/month</span>
            <span className="payment-mobile-billing-info">Billed annually at $96</span>
            <span className="payment-mobile-savings">Save 33% ($48/year)</span>
          </motion.button>
          <motion.button
            className={`payment-mobile-subscription-option ${localSubscriptionPlan === '3months' ? 'payment-mobile-selected' : ''}`}
            onClick={() => handleSubscriptionChange('3months')}
            whileTap={{ scale: 0.95 }}
          >
            <h3 className="payment-mobile-subscription-title">3-Month Plan</h3>
            <span className="payment-mobile-price">$12/month</span>
            <span className="payment-mobile-billing-info">Billed quarterly at $36</span>
          </motion.button>
        </div>
        
        <div className="payment-mobile-line-adjuster">
          <span>Number of lines</span>
          <div className="payment-mobile-line-controls">
            <motion.button onClick={() => handleLineChange(-1)} whileTap={{ scale: 0.9 }}><Minus size={20} /></motion.button>
            <span>{localLines}</span>
            <motion.button onClick={() => handleLineChange(1)} whileTap={{ scale: 0.9 }}><Plus size={20} /></motion.button>
          </div>
        </div>

        <div className="payment-mobile-simplified-cost">
          <h3>Your total</h3>
          <CostItem label="Monthly Verizon Cost" value={costs.estimatedMonthlyVerizonTotal} />
          <CostItem label="Total Due Now" value={costs.totalDueNow} isTotal={true} />
          <motion.button 
            className="payment-mobile-price-breakdown-button" 
            onClick={() => setShowPriceBreakdown(!showPriceBreakdown)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Price breakdown {showPriceBreakdown ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </motion.button>
        </div>

        <AnimatePresence>
          {showPriceBreakdown && <PriceBreakdown onClose={() => setShowPriceBreakdown(false)} />}
        </AnimatePresence>
      </div>

      <div className="payment-mobile-plan-summary">
        <div className="payment-mobile-plan-info">
          <div className="payment-mobile-plan-info-left">
            <span className="payment-mobile-plan-icon">
              <Zap size={16} />
            </span>
            <h3>{selectedPlan.title || 'Verizon - Unlimited Welcome'}</h3>
          </div>
          <p className="payment-mobile-plan-price">
            ${costs.estimatedMonthlyVerizonTotal.toFixed(2)}/mo
          </p>
        </div>
        <motion.button 
          className="payment-mobile-reserve-button" 
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => {
            console.log('Plan reserved:', selectedPlan, 'Subscription:', localSubscriptionPlan, 'Lines:', localLines);
          }}
        >
          Confirm and pay
        </motion.button>
      </div>
    </div>
  );
};

const CostItem = ({ label, value, isTotal = false, className = '' }) => (
  <div className={`payment-mobile-cost-item ${isTotal ? 'payment-mobile-total' : ''} ${className}`}>
    <span className="payment-mobile-cost-label">{label}</span>
    <span className="payment-mobile-cost-value">${value.toFixed(2)}</span>
  </div>
);

export default PaymentMobile;